import { EventType } from '@portal/enum/analytics';
import { sendEvent } from '@portal/services/firebase';
import { Dispatch } from 'redux';

import FavoredContactApi from '~/repositories/favoredContact';
import * as MessageService from '~/services/message';

import {
  FAVORED_CONTACT_DELETE,
  FAVORED_CONTACT_FAVORITE,
  FAVORED_CONTACT_LIST,
  SET_NEW_CONTACT,
} from './actionTypes';
import { decreaseLoading, increaseLoading, LoadingAction } from './loading';

export type SetContactsAction = {
  type: string;
  payload: models.PaginationResponse<models.Contact>;
};

export const getFavoredContact =
  () => async (dispatch: Dispatch<SetContactsAction | LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      const payload = await FavoredContactApi.getFavoredContact();

      dispatch({
        payload,
        type: FAVORED_CONTACT_LIST,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export type CreateContactSuccessful = {
  type: string;
  payload: models.Contact;
};

export const createFavoredContact =
  (params: models.Contact) =>
  async (dispatch: Dispatch<LoadingAction | CreateContactSuccessful>) => {
    dispatch(increaseLoading());
    try {
      delete params.type;

      const payload = await FavoredContactApi.createFavoredContact(params);
      sendEvent(EventType.CREATE_CONTACT);
      MessageService.success(
        'COMPONENTS.CONTACT_LIST.NEW_CONTACT.MESSAGES.SUCCESS'
      );
      dispatch({
        type: SET_NEW_CONTACT,
        payload,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(`CONTACTS.${err.message}`, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export type DeleteContactsAction = {
  type: string;
  payload: string;
};

export const deleteFavoredContact =
  (id: string) =>
  async (dispatch: Dispatch<DeleteContactsAction | LoadingAction>) => {
    try {
      await FavoredContactApi.deleteFavoredContact(id);

      dispatch({
        type: FAVORED_CONTACT_DELETE,
        payload: id,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    }
  };

export type ChangeFavoriteContactsAction = {
  type: string;
  payload: { id: string; isFavorite: boolean };
};

export const favoriteFavoredContact =
  (isFavorite: boolean, id: string) =>
  async (dispatch: Dispatch<ChangeFavoriteContactsAction | LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      await FavoredContactApi.favoriteFavoredContact({ isFavorite }, id);

      dispatch({
        type: FAVORED_CONTACT_FAVORITE,
        payload: {
          id,
          isFavorite,
        },
      });
      MessageService.success(
        isFavorite
          ? 'PAGES.HOME.TRANSFER.FAVORED.MESSAGES.FAVORITE_SUCCESS'
          : 'PAGES.HOME.TRANSFER.FAVORED.MESSAGES.UNFAVORITE_SUCCESS'
      );
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };
